import { useMutation } from '@apollo/client';
import { FormInstance } from 'antd/lib/form';
import { UploadFile } from 'antd/es/upload/interface';
// Api
import {
  ADMIN_CREATE_EXT_TALENT,
  ADMIN_CREATE_EXT_TALENT_AVATAR_PRESIGNED_URL,
  ADMIN_CREATE_EXT_TALENT_CONTACT,
  ADMIN_CREATE_EXT_TALENT_SOCIAL,
  ADMIN_UPDATE_EXT_TALENT,
  ADMIN_UPDATE_EXT_TALENT_CONTACT,
  ADMIN_UPDATE_EXT_TALENT_SOCIAL,
} from 'api/ExtTalent/mutations';
// Types
import {
  AdminCreateExtTalent,
  AdminCreateExtTalentVariables,
} from 'api/ExtTalent/types/AdminCreateExtTalent';
import {
  AdminCreateExtTalentAvatarPresignedUrl,
  AdminCreateExtTalentAvatarPresignedUrlVariables,
} from 'api/ExtTalent/types/AdminCreateExtTalentAvatarPresignedUrl';
import {
  AdminCreateExtTalentContact,
  AdminCreateExtTalentContactVariables,
} from 'api/ExtTalent/types/AdminCreateExtTalentContact';
import {
  AdminCreateExtTalentSocial,
  AdminCreateExtTalentSocialVariables,
} from 'api/ExtTalent/types/AdminCreateExtTalentSocial';
import {
  AdminUpdateExtTalent,
  AdminUpdateExtTalentVariables,
} from 'api/ExtTalent/types/AdminUpdateExtTalent';
import {
  AdminUpdateExtTalentContact,
  AdminUpdateExtTalentContactVariables,
} from 'api/ExtTalent/types/AdminUpdateExtTalentContact';
import {
  AdminUpdateExtTalentSocial,
  AdminUpdateExtTalentSocialVariables,
} from 'api/ExtTalent/types/AdminUpdateExtTalentSocial';
import {
  AdminCreateExtTalentInput,
  AdminCreateExtTalentSocialInput,
  AdminUpdateExtTalentInput,
  AdminUpdateExtTalentSocialInput,
  ExtTalentContactStatus,
  ExtTalentLinkStatus,
  ExtTalentStatus,
} from 'api/graphql-global-types';
import {
  ContactFormValue,
  ExternalTalent,
  ExternalTalentFormValues,
  SocialFormValue,
} from 'helpers/externalTalents';
// Helpers
import {
  getFileExtension,
  getExternalTalentSocialHandle,
  normalizeNumber,
  normalizeRequiredString,
  normalizeString,
} from 'helpers/externalTalents';
// UI
import { errorNotification, successNotification } from 'ui/Notification';

type UseExternalTalentModalOptions = {
  avatarFileList: UploadFile[];
  form: FormInstance<ExternalTalentFormValues>;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
  talent?: ExternalTalent | null;
};

type UseExternalTalentModalResult = {
  handleSubmit: () => Promise<void>;
  isSubmitting: boolean;
};

const isFormValidationError = (error: unknown): boolean =>
  Boolean(
    error &&
      typeof error === 'object' &&
      'errorFields' in error &&
      Array.isArray((error as { errorFields?: unknown[] }).errorFields)
  );

const getSyncIssueMessage = (
  entityName: 'social' | 'contact',
  failures: string[]
): string | null => {
  if (!failures.length) {
    return null;
  }

  const pluralSuffix = failures.length === 1 ? '' : 's';
  return `${
    failures.length
  } ${entityName}${pluralSuffix} failed to sync (${failures.join(', ')}).`;
};

export const useExternalTalentModal = ({
  avatarFileList,
  form,
  onClose,
  onSuccess,
  talent,
}: UseExternalTalentModalOptions): UseExternalTalentModalResult => {
  const [createExtTalent, { loading: createExtTalentLoading }] = useMutation<
    AdminCreateExtTalent,
    AdminCreateExtTalentVariables
  >(ADMIN_CREATE_EXT_TALENT);

  const [updateExtTalent, { loading: updateExtTalentLoading }] = useMutation<
    AdminUpdateExtTalent,
    AdminUpdateExtTalentVariables
  >(ADMIN_UPDATE_EXT_TALENT);

  const [createExtTalentSocial, { loading: createExtTalentSocialLoading }] =
    useMutation<
      AdminCreateExtTalentSocial,
      AdminCreateExtTalentSocialVariables
    >(ADMIN_CREATE_EXT_TALENT_SOCIAL);

  const [updateExtTalentSocial, { loading: updateExtTalentSocialLoading }] =
    useMutation<
      AdminUpdateExtTalentSocial,
      AdminUpdateExtTalentSocialVariables
    >(ADMIN_UPDATE_EXT_TALENT_SOCIAL);

  const [createExtTalentContact, { loading: createExtTalentContactLoading }] =
    useMutation<
      AdminCreateExtTalentContact,
      AdminCreateExtTalentContactVariables
    >(ADMIN_CREATE_EXT_TALENT_CONTACT);

  const [updateExtTalentContact, { loading: updateExtTalentContactLoading }] =
    useMutation<
      AdminUpdateExtTalentContact,
      AdminUpdateExtTalentContactVariables
    >(ADMIN_UPDATE_EXT_TALENT_CONTACT);

  const [createAvatarPresignedUrl, { loading: createAvatarUploadLoading }] =
    useMutation<
      AdminCreateExtTalentAvatarPresignedUrl,
      AdminCreateExtTalentAvatarPresignedUrlVariables
    >(ADMIN_CREATE_EXT_TALENT_AVATAR_PRESIGNED_URL);

  const isSubmitting =
    createExtTalentLoading ||
    updateExtTalentLoading ||
    createExtTalentSocialLoading ||
    updateExtTalentSocialLoading ||
    createExtTalentContactLoading ||
    updateExtTalentContactLoading ||
    createAvatarUploadLoading;

  const buildBaseTalentInput = (values: ExternalTalentFormValues) => ({
    athleticLevel: values.athleticLevel || null,
    birthDate: normalizeNumber(values.birthDate),
    birthMonth: normalizeNumber(values.birthMonth),
    birthYear: normalizeNumber(values.birthYear),
    city: normalizeString(values.city),
    collegeUniversity: normalizeString(values.collegeUniversity),
    collegeUniversityRaw: normalizeString(values.collegeUniversityRaw),
    country: normalizeString(values.country),
    firstName: normalizeRequiredString(values.firstName),
    gender: values.gender || null,
    homeCity: normalizeString(values.homeCity),
    homeCountry: normalizeString(values.homeCountry),
    homeState: normalizeString(values.homeState),
    lastName: normalizeRequiredString(values.lastName),
    league: normalizeString(values.league),
    leagueRaw: normalizeString(values.leagueRaw),
    middleName: normalizeString(values.middleName),
    sport: normalizeString(values.sport),
    state: normalizeString(values.state),
    team: normalizeString(values.team),
    teamRaw: normalizeString(values.teamRaw),
  });

  const buildCreateTalentInput = (
    values: ExternalTalentFormValues
  ): AdminCreateExtTalentInput => buildBaseTalentInput(values);

  const buildUpdateTalentInput = (
    values: ExternalTalentFormValues
  ): AdminUpdateExtTalentInput => ({
    ...buildBaseTalentInput(values),
    id: talent?.id || '',
    status: values.status || ExtTalentStatus.active,
  });

  const uploadAvatarToS3 = async (extTalentId: string, file: File) => {
    const extension = getFileExtension(file);

    const { data } = await createAvatarPresignedUrl({
      variables: {
        input: {
          ext: extension,
          extTalentId,
        },
      },
    });

    const uploadData = data?.adminCreateExtTalentAvatarPresignedUrl;

    if (!uploadData) {
      throw new Error('Failed to prepare avatar upload');
    }

    let uploadFields: Record<string, string>;

    try {
      uploadFields = JSON.parse(uploadData.fields) as Record<string, string>;
    } catch (error) {
      throw new Error('Failed to parse avatar upload fields');
    }

    const formData = new FormData();

    Object.entries(uploadFields).forEach(([key, value]) => {
      formData.append(key, value);
    });

    formData.append('key', uploadData.key);
    formData.append('Content-Type', file.type);
    formData.append('file', file);

    const response = await fetch(uploadData.url, {
      body: formData,
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error('Avatar upload failed');
    }

    return uploadData.key;
  };

  const syncSocials = async (
    extTalentId: string,
    socials: SocialFormValue[]
  ): Promise<string[]> => {
    const failures: string[] = [];

    for (let index = 0; index < socials.length; index += 1) {
      const social = socials[index];

      try {
        if (social.id) {
          const updateInput: AdminUpdateExtTalentSocialInput = {
            followersCount: normalizeNumber(social.followersCount),
            id: social.id,
            profileName: normalizeString(social.profileName),
            status: social.status || ExtTalentLinkStatus.active,
          };

          await updateExtTalentSocial({
            variables: {
              input: updateInput,
            },
          });

          continue;
        }

        const socialHandle = getExternalTalentSocialHandle(
          social.url,
          social.platform
        );

        if (!socialHandle || !social.platform) {
          continue;
        }

        const createInput: AdminCreateExtTalentSocialInput = {
          extTalentId,
          followersCount: normalizeNumber(social.followersCount),
          handle: normalizeRequiredString(socialHandle),
          platform: social.platform,
          profileName: normalizeString(social.profileName),
          status: social.status || ExtTalentLinkStatus.active,
        };

        await createExtTalentSocial({
          variables: {
            input: createInput,
          },
        });
      } catch (error) {
        failures.push(social.url || social.profileName || `#${index + 1}`);
      }
    }

    return failures;
  };

  const syncContacts = async (
    extTalentId: string,
    contacts: ContactFormValue[]
  ): Promise<string[]> => {
    const failures: string[] = [];

    for (let index = 0; index < contacts.length; index += 1) {
      const contact = contacts[index];

      try {
        if (contact.id) {
          await updateExtTalentContact({
            variables: {
              input: {
                agentName: normalizeString(contact.agentName),
                id: contact.id,
                status: contact.status || ExtTalentContactStatus.active,
              },
            },
          });

          continue;
        }

        if (!contact.type || !contact.value) {
          continue;
        }

        await createExtTalentContact({
          variables: {
            input: {
              agentName: normalizeString(contact.agentName),
              extTalentId,
              role: contact.role || null,
              type: contact.type,
              value: normalizeRequiredString(contact.value),
            },
          },
        });
      } catch (error) {
        failures.push(contact.value || `#${index + 1}`);
      }
    }

    return failures;
  };

  const saveTalent = async (
    values: ExternalTalentFormValues
  ): Promise<string> => {
    if (talent?.id) {
      await updateExtTalent({
        variables: {
          input: buildUpdateTalentInput(values),
        },
      });

      return talent.id;
    }

    const { data } = await createExtTalent({
      variables: {
        input: buildCreateTalentInput(values),
      },
    });

    return data?.adminCreateExtTalent?.id || '';
  };

  const syncRelatedRecords = async (
    extTalentId: string,
    values: ExternalTalentFormValues
  ) => {
    const [socialFailures, contactFailures] = await Promise.all([
      syncSocials(extTalentId, values.socials || []),
      syncContacts(extTalentId, values.contacts || []),
    ]);

    return { contactFailures, socialFailures };
  };

  const buildIssueMessages = ({
    contactFailures,
    socialFailures,
  }: {
    contactFailures: string[];
    socialFailures: string[];
  }): string[] => {
    const issues: string[] = [];
    const socialIssueMessage = getSyncIssueMessage('social', socialFailures);
    const contactIssueMessage = getSyncIssueMessage('contact', contactFailures);

    if (socialIssueMessage) {
      issues.push(socialIssueMessage);
    }

    if (contactIssueMessage) {
      issues.push(contactIssueMessage);
    }

    return issues;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const avatarFile = avatarFileList?.[0]?.originFileObj as File | undefined;
      const issues: string[] = [];
      const extTalentId = await saveTalent(values);

      if (!extTalentId) {
        throw new Error('Missing external talent id');
      }

      if (avatarFile) {
        try {
          const avatarKey = await uploadAvatarToS3(extTalentId, avatarFile);

          await updateExtTalent({
            variables: {
              input: {
                avatarKey,
                id: extTalentId,
              },
            },
          });
        } catch (error) {
          issues.push(
            'Avatar upload failed, but the external talent details were saved.'
          );
        }
      }

      issues.push(
        ...buildIssueMessages(await syncRelatedRecords(extTalentId, values))
      );

      successNotification(
        issues.length
          ? `External talent ${
              talent ? 'updated' : 'created'
            } with some follow-up issues`
          : `External talent ${talent ? 'updated' : 'created'} successfully`
      );

      onClose();

      try {
        await onSuccess();
      } catch (error) {
        issues.push('The external talents list failed to refresh.');
      }

      if (issues.length) {
        errorNotification(issues.join(' '));
      }
    } catch (error) {
      if (isFormValidationError(error)) {
        return;
      }

      errorNotification(
        (error as Error)?.message ||
          'Failed to save external talent. Please review the entered data and try again.'
      );
    }
  };

  return {
    handleSubmit,
    isSubmitting,
  };
};
